import React from "react";

function parseBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1" || normalized === "yes") return true;
    if (normalized === "false" || normalized === "0" || normalized === "no") return false;
  }
  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  return undefined;
}

function readRuntimeFlag(name: string): boolean | undefined {
  if (typeof window !== "undefined" && window && name in window) {
    const maybeValue = (window as any)[name];
    const parsed = parseBoolean(maybeValue);
    if (parsed !== undefined) return parsed;
  }

  if (typeof process !== "undefined" && process?.env && name in process.env) {
    const parsed = parseBoolean(process.env[name]);
    if (parsed !== undefined) return parsed;
  }

  if (typeof import.meta !== "undefined" && (import.meta as any)?.env && name in (import.meta as any).env) {
    const parsed = parseBoolean((import.meta as any).env[name]);
    if (parsed !== undefined) return parsed;
  }

  return undefined;
}

function getStorage(): Storage | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") return null;
  try {
    return window.localStorage;
  } catch (error) {
    console.warn("LocalStorage unavailable", error);
    return null;
  }
}

function readStoredFlag(name: string): boolean | undefined {
  const storage = getStorage();
  if (!storage) return undefined;
  const stored = storage.getItem(`studio.flags.${name}`);
  const parsed = parseBoolean(stored);
  return parsed;
}

export function getFlag(name: string, defaultValue = false): boolean {
  const stored = readStoredFlag(name);
  if (stored !== undefined) return stored;

  const runtime = readRuntimeFlag(name);
  if (runtime !== undefined) return runtime;

  return defaultValue;
}

export function setFlag(name: string, value: boolean): void {
  const storage = getStorage();
  if (storage) {
    storage.setItem(`studio.flags.${name}`, String(value));
  }

  if (typeof window !== "undefined") {
    const event = new CustomEvent("feature-flag-changed", { detail: { name, value } });
    window.dispatchEvent(event);
  }
}

export function useFeatureFlag(name: string, defaultValue = false): [boolean, (value: boolean) => void] {
  const [value, setValue] = React.useState<boolean>(() => getFlag(name, defaultValue));

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key !== `studio.flags.${name}`) return;
      setValue(getFlag(name, defaultValue));
    };

    const handleCustomEvent = (event: Event) => {
      const detail = (event as CustomEvent<{ name: string; value: boolean }>).detail;
      if (!detail || detail.name !== name) return;
      setValue(detail.value);
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("feature-flag-changed", handleCustomEvent);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("feature-flag-changed", handleCustomEvent);
    };
  }, [defaultValue, name]);

  const setAndPersist = (next: boolean) => {
    setValue(next);
    setFlag(name, next);
  };

  return [value, setAndPersist];
}

export const FEATURE_NAV_V01: boolean = getFlag("FEATURE_NAV_V01", true);
export const FEATURE_COCKPIT_V01: boolean = getFlag("FEATURE_COCKPIT_V01", true);
export const FEATURE_JARVIS_THEME_NAME = "FEATURE_JARVIS_THEME";
export const FEATURE_JARVIS_THEME: boolean = getFlag(FEATURE_JARVIS_THEME_NAME, true);
export const FEATURE_JARVIS_V2_NAME = "LONGWALLS_JARVIS_V2_ENABLED";
export const FEATURE_JARVIS_V2: boolean = getFlag(FEATURE_JARVIS_V2_NAME, true);
export const FEATURE_INTERNAL_ADMIN_NAME = "ENABLE_INTERNAL_ADMIN";
export const FEATURE_INTERNAL_ADMIN: boolean = getFlag(FEATURE_INTERNAL_ADMIN_NAME, false);
export const DISABLE_COCKPIT_FIXTURES: boolean = getFlag("DISABLE_COCKPIT_FIXTURES", false);
